import { useState } from "react";
import { api } from "@/lib/api";

/**
 * Modal de refinado de nombres de personaje.
 *
 * Flujo:
 *   1. Click en "🪄 Refinar nombres" → llama a `/refine-names` (IA agrupa)
 *   2. Muestra los grupos detectados (con confianza por variante)
 *   3. Usuario edita el nombre canónico, marca/desmarca variantes
 *   4. Click "Aplicar" → llama a `/apply-refine-names` con el mapping final
 *   5. Cierra modal y refresca el screenplay
 *
 * Vive FUERA del editor protegido. Se monta desde el panel de Personajes.
 */
export default function CharacterRefineModal({ open, onClose, projectId, onApplied }) {
  // null = sin escanear todavía; objeto = resultado del escaneo
  const [state, setState] = useState({ phase: "idle", error: "", result: null });
  // Estado editable del usuario: para cada grupo, qué variantes están activas
  // y cuál es el canonical editado. Se inicializa al cargar el resultado.
  const [groupState, setGroupState] = useState({});

  const reset = () => {
    setState({ phase: "idle", error: "", result: null });
    setGroupState({});
  };

  const scan = async () => {
    setState({ phase: "scanning", error: "", result: null });
    try {
      const r = await api.post(`/icaa/projects/${projectId}/screenplay/characters/refine-names`);
      const data = r.data;
      // Inicializa el estado editable: todas las variantes activas por defecto,
      // canonical pre-rellenado con el que sugiere la IA.
      const gs = {};
      (data.groups || []).forEach((g, i) => {
        gs[i] = {
          canonical: g.canonical,
          variants: Object.fromEntries(g.variants.map((v) => [v.name, true])),
        };
      });
      setGroupState(gs);
      setState({ phase: "review", error: "", result: data });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error al refinar";
      setState({ phase: "idle", error: String(msg), result: null });
    }
  };

  const toggleVariant = (gi, name) => {
    setGroupState((prev) => ({
      ...prev,
      [gi]: {
        ...prev[gi],
        variants: { ...prev[gi].variants, [name]: !prev[gi].variants[name] },
      },
    }));
  };

  const updateCanonical = (gi, v) => {
    setGroupState((prev) => ({ ...prev, [gi]: { ...prev[gi], canonical: v } }));
  };

  // Construye el mapping final a partir del estado editable. Solo incluye
  // variantes marcadas y donde la variante ≠ canonical (para no hacer trabajo
  // innecesario).
  const buildMapping = () => {
    const map = {};
    (state.result?.groups || []).forEach((g, i) => {
      const gs = groupState[i];
      if (!gs) return;
      const canonical = (gs.canonical || "").trim().toUpperCase();
      if (!canonical) return;
      g.variants.forEach((v) => {
        const active = gs.variants[v.name];
        if (active && v.name !== canonical) {
          map[v.name] = canonical;
        }
      });
    });
    return map;
  };

  const mapping = state.phase === "review" ? buildMapping() : {};
  const changeCount = Object.keys(mapping).length;

  const apply = async () => {
    if (changeCount === 0) {
      onClose?.();
      return;
    }
    setState({ ...state, phase: "applying" });
    try {
      const r = await api.post(
        `/icaa/projects/${projectId}/screenplay/characters/apply-refine-names`,
        { mapping }
      );
      onApplied?.(r.data);
      reset();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error al aplicar";
      setState({ phase: "review", error: String(msg), result: state.result });
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={() => { if (state.phase !== "applying" && state.phase !== "scanning") { reset(); onClose?.(); } }}
      data-testid="char-refine-modal"
    >
      <div
        className="relative flex h-[88vh] w-[860px] max-w-full flex-col overflow-hidden rounded-xl bg-[#FAFAF6] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 border-b border-stone-200 bg-white px-5 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
              Refinar nombres de personaje
            </p>
            <p className="mt-0.5 font-serif text-base text-stone-900">
              Detecta variantes y erratas en todo el guion
            </p>
          </div>
          <button
            onClick={() => { if (state.phase !== "applying" && state.phase !== "scanning") { reset(); onClose?.(); } }}
            data-testid="char-refine-close"
            className="rounded-full border border-stone-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-[#941620] hover:text-[#941620]"
          >
            ✕ Cerrar
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {state.phase === "idle" && (
            <IdleView onScan={scan} error={state.error} />
          )}
          {state.phase === "scanning" && <BusyView label="Analizando el guion con IA…" />}
          {state.phase === "applying" && <BusyView label="Aplicando cambios al guion…" />}
          {state.phase === "review" && (
            <ReviewView
              result={state.result}
              groupState={groupState}
              onToggleVariant={toggleVariant}
              onUpdateCanonical={updateCanonical}
              error={state.error}
            />
          )}
        </div>

        {state.phase === "review" && (
          <footer className="border-t border-stone-200 bg-white px-5 py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-stone-600">
                {changeCount === 0
                  ? "No hay cambios marcados. Activa al menos una variante para aplicar."
                  : `${changeCount} sustitución${changeCount === 1 ? "" : "es"} pendiente${changeCount === 1 ? "" : "s"} de aplicar al guion.`}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { reset(); onClose?.(); }}
                  data-testid="char-refine-cancel"
                  className="rounded-full border border-stone-300 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-stone-500"
                >
                  Cancelar
                </button>
                <button
                  onClick={apply}
                  disabled={changeCount === 0}
                  data-testid="char-refine-apply"
                  className="rounded-full bg-[#941620] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#7a1019] disabled:cursor-not-allowed disabled:bg-stone-300"
                >
                  Aplicar al guion
                </button>
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}

function IdleView({ onScan, error }) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div className="mx-auto max-w-md">
        <div className="text-[3rem] leading-none">🪄</div>
        <h3 className="mt-3 font-serif text-2xl text-stone-900">Limpia los nombres de un solo click</h3>
        <p className="mt-3 text-[13px] leading-relaxed text-stone-600">
          La IA escanea todas las líneas de personaje del guion y detecta variantes
          del mismo nombre (CARLOS vs CARLOS SALORD), erratas (PEDORO → PEDRO) y
          formas descriptivas (PADRE / EL PADRE / PADRE DE CARLOS). Tú decides cuáles
          unificar y con qué nombre canónico antes de aplicar.
        </p>
        {error && (
          <p className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
            {error}
          </p>
        )}
        <button
          onClick={onScan}
          data-testid="char-refine-scan"
          className="mt-6 rounded-full bg-[#941620] px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#7a1019]"
        >
          Analizar guion con IA
        </button>
      </div>
    </div>
  );
}

function BusyView({ label }) {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-[#941620]" />
      <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-stone-500">{label}</p>
    </div>
  );
}

function ReviewView({ result, groupState, onToggleVariant, onUpdateCanonical, error }) {
  const groups = result?.groups || [];
  const untouched = result?.untouched || [];
  return (
    <div className="space-y-4">
      {error && (
        <p className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
          {error}
        </p>
      )}

      <div className="grid grid-cols-3 gap-3 text-center">
        <Stat label="Nombres únicos" value={result?.total_names || 0} />
        <Stat label="Grupos detectados" value={groups.length} accent="#941620" />
        <Stat label="Sin cambios" value={untouched.length} muted />
      </div>

      {groups.length === 0 ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-900">
          ✓ Buen trabajo — la IA no ha detectado variantes ni erratas en el guion.
          Los <strong>{untouched.length}</strong> nombres de personaje son únicos y consistentes.
        </div>
      ) : (
        <div className="space-y-3" data-testid="char-refine-groups">
          {groups.map((g, gi) => {
            const gs = groupState[gi] || { canonical: g.canonical, variants: {} };
            const activeCount = Object.values(gs.variants).filter(Boolean).length;
            return (
              <div
                key={gi}
                className="overflow-hidden rounded-lg border border-stone-200 bg-white"
                data-testid={`char-refine-group-${gi}`}
              >
                <div className="border-b border-stone-200 bg-stone-50 px-4 py-2">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
                    Grupo {gi + 1} · {activeCount} variante{activeCount === 1 ? "" : "s"} activa{activeCount === 1 ? "" : "s"}
                  </p>
                  <p className="mt-0.5 text-[11px] italic text-stone-600">{g.reason}</p>
                </div>
                <div className="px-4 py-3">
                  <label className="text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500">
                    Nombre canónico
                  </label>
                  <input
                    value={gs.canonical}
                    onChange={(e) => onUpdateCanonical(gi, e.target.value)}
                    data-testid={`char-refine-canonical-${gi}`}
                    className="mt-1 w-full rounded border border-stone-300 bg-white px-3 py-1.5 font-mono text-sm font-bold uppercase tracking-wider text-stone-900 outline-none focus:border-[#941620]"
                  />
                  <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500">
                    Variantes detectadas en el guion
                  </p>
                  <ul className="mt-1 space-y-1">
                    {g.variants.map((v) => {
                      const active = !!gs.variants[v.name];
                      const isSame = v.name === gs.canonical;
                      return (
                        <li key={v.name}>
                          <label
                            className={`flex cursor-pointer items-center gap-3 rounded border px-3 py-1.5 text-[12px] transition ${
                              active
                                ? "border-[#941620]/40 bg-[#941620]/5"
                                : "border-stone-200 bg-stone-50/60 opacity-60"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={active}
                              onChange={() => onToggleVariant(gi, v.name)}
                              data-testid={`char-refine-variant-${gi}-${v.name}`}
                              className="h-4 w-4 accent-[#941620]"
                            />
                            <span className="flex-1 font-mono uppercase tracking-wider text-stone-900">{v.name}</span>
                            <span className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
                              {v.count} {v.count === 1 ? "uso" : "usos"}
                            </span>
                            <ConfidenceBadge value={v.confidence} />
                            {isSame && (
                              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.15em] text-emerald-700">
                                = canónico
                              </span>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {untouched.length > 0 && (
        <details className="rounded-lg border border-stone-200 bg-white px-4 py-3">
          <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500">
            Nombres sin cambios ({untouched.length})
          </summary>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-wider text-stone-700">
            {untouched.join(" · ")}
          </p>
        </details>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "#26775E", muted = false }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${muted ? "border-stone-200 bg-stone-50" : "border-stone-200 bg-white"}`}>
      <p className="text-[9px] font-semibold uppercase tracking-[0.22em] text-stone-500">{label}</p>
      <p className="mt-0.5 font-serif text-2xl" style={{ color: muted ? "#78716c" : accent }}>{value}</p>
    </div>
  );
}

function ConfidenceBadge({ value }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const tone =
    pct >= 90 ? "bg-emerald-100 text-emerald-800" :
    pct >= 75 ? "bg-amber-100 text-amber-800" :
                "bg-rose-100 text-rose-800";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.15em] ${tone}`}>
      {pct}%
    </span>
  );
}
